import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import FloatingBotanicals from '@/components/FloatingBotanicals';
import { HERO } from '@/constants/testIds';

const BADGES = [
  'Better sleep',
  'Hormonal wellness',
  'Digestion',
  'Stress relief',
  'Immunity',
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={HERO.root}
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="blob absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-[#2D8B5B]" style={{ opacity: 0.18 }} />
      <div className="blob absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-[#C97B84]" style={{ opacity: 0.15 }} />

      <FloatingBotanicals />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div style={{ y, opacity }} className="lg:col-span-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#1D7448]/25 bg-[#FAF4E3]/60 backdrop-blur px-4 py-2 text-xs uppercase tracking-[0.22em] text-[#1D7448]"
            >
              <Sparkles className="w-3.5 h-3.5" />
              A daily ritual, quietly powerful
            </motion.div>

            <motion.h1
              data-testid={HERO.headline}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-serif mt-6 text-[44px] leading-[1.02] sm:text-6xl lg:text-[80px] text-[#1D7448] font-light"
            >
              Ancient herbs
              <br />
              <span className="italic font-normal">for modern</span>
              <br />
              lifestyle.
            </motion.h1>

            <motion.p
              data-testid={HERO.sub}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-7 max-w-md text-lg text-[#4A6756] leading-relaxed"
            >
              Daily tea rituals crafted to support your body naturally — from
              your first deep breath in the morning to the softest end of your
              day.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <button
                data-testid={HERO.ctaPrimary}
                onClick={() => go('shop')}
                className="btn-shine group inline-flex items-center gap-2 bg-[#2D8B5B] hover:bg-[#247149] text-[#FAF4E3] px-7 py-4 rounded-full text-[15px] font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-[0_16px_30px_-12px_rgba(45,139,91,0.6)]"
              >
                Shop Now
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid={HERO.ctaSecondary}
                onClick={() => go('quiz')}
                className="inline-flex items-center gap-2 border border-[#1D7448] text-[#1D7448] hover:bg-[#1D7448] hover:text-[#FAF4E3] px-7 py-4 rounded-full text-[15px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
              >
                Take Tea Quiz
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="mt-10 flex flex-wrap gap-2"
            >
              {BADGES.map((b) => (
                <span
                  key={b}
                  className="text-xs uppercase tracking-[0.16em] text-[#1D7448]/80 px-3 py-1.5 rounded-full border border-[#1D7448]/20 bg-[#FDFBF7]/70"
                >
                  {b}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <div className="lg:col-span-6 relative">
            <motion.div
              style={{ y: yImage }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] rounded-[36px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(13,58,36,0.35)]"
            >
              <img
                src="https://images.pexels.com/photos/8329955/pexels-photo-8329955.jpeg?auto=compress&cs=tinysrgb&w=1400"
                alt="Woman holding a warm cup of tea in soft morning light"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D3A24]/30 via-transparent to-transparent" />

              {/* Steam accents */}
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-3 pointer-events-none">
                <span className="steam w-2 h-16 rounded-full bg-white/70" style={{ animationDelay: '0s' }} />
                <span className="steam w-2 h-16 rounded-full bg-white/60" style={{ animationDelay: '0.9s' }} />
                <span className="steam w-2 h-16 rounded-full bg-white/50" style={{ animationDelay: '1.6s' }} />
              </div>

              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-64 bg-[#FAF4E3]/95 backdrop-blur-md rounded-2xl p-5 shadow-lg border border-[#E5DCC3]"
              >
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#2D8B5B]">Made with</p>
                <p className="font-serif text-2xl text-[#1D7448] mt-1 leading-tight">100% natural ingredients</p>
                <p className="text-xs text-[#4A6756] mt-1">Ashwagandha · Chamomile · Turmeric</p>
              </motion.div>
            </motion.div>

            {/* Small decorative image */}
            <motion.div
              animate={{ y: [0, -12, 0], rotate: [0, 3, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
              className="hidden md:block absolute -top-6 -left-6 w-32 h-32 rounded-full overflow-hidden ring-4 ring-[#FAF4E3] shadow-xl"
            >
              <img
                src="https://images.pexels.com/photos/13641676/pexels-photo-13641676.jpeg?auto=compress&cs=tinysrgb&w=400"
                alt="Hibiscus petals"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
